import { Link, useParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { QualifyingItem, RaceDetail, ResultItem } from '../api/types'
import { DataState } from '../components/DataState'
import { formatPoints } from '../lib/decimal'
import { useUniversePath } from '../lib/universe'
import { useUniverseResults, type Universe } from './PeoplePages'

export function RacePage() {
  const { year: rawYear = '', round: rawRound = '' } = useParams()
  const year = Number(rawYear)
  const round_number = Number(rawRound)
  const path = { path: { year, round_number } }

  const race = useApi<RaceDetail>(['race', year, round_number], () =>
    api.GET('/api/v1/races/{year}/{round_number}', { params: path }),
  )
  // These three return bare arrays rather than a page, unlike every list above.
  const results = useApi<ResultItem[]>(['race-results', year, round_number], () =>
    api.GET('/api/v1/races/{year}/{round_number}/results', { params: path }),
  )
  const qualifying = useApi<QualifyingItem[]>(['race-qualifying', year, round_number], () =>
    api.GET('/api/v1/races/{year}/{round_number}/qualifying', { params: path }),
  )

  const sprint = (results.data ?? []).filter((row) => row.session_type === 'SPRINT_RACE')
  const grandPrix = (results.data ?? []).filter((row) => row.session_type === 'RACE')

  return (
    <section>
      <header className="page-head">
        <h1>{race.data?.official_name ?? `${rawYear} round ${rawRound}`}</h1>
        <p className="page-head__aside">
          <Link to={`/seasons/${year}?tab=calendar`}>← {rawYear} calendar</Link>
        </p>
      </header>
      {race.data ? (
        <p className="muted">
          <Facts race={race.data} />
          {race.data.drivers_championship_decider ? ' · decided the championship' : ''}
        </p>
      ) : null}

      <h2 className="section-title">Race</h2>
      <DataState query={results} empty="No results are recorded for this race.">
        {() => <ResultsTable rows={grandPrix} />}
      </DataState>

      {sprint.length > 0 ? (
        <>
          <h2 className="section-title">Sprint</h2>
          <ResultsTable rows={sprint} />
        </>
      ) : null}

      <details className="fold">
        <summary>Qualifying</summary>
        <DataState query={qualifying} empty="No qualifying times are recorded.">
          {(rows) => (
            <table className="grid">
              <thead>
                <tr>
                  <th className="num">Pos</th>
                  <th>Driver</th>
                  <th>Constructor</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.driver.slug}-${row.position.display_order}`}>
                    <td className="num">{row.position.number ?? row.position.text}</td>
                    <td>
                      <Link to={`/drivers/${row.driver.slug}`}>{row.driver.name}</Link>
                    </td>
                    <td className="muted">{row.constructor.name}</td>
                    <td className="muted">{row.q3 ?? row.q2 ?? row.q1 ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DataState>
      </details>
    </section>
  )
}

function Facts({ race }: { race: RaceDetail }) {
  return (
    <>
      Round {race.round} · {race.date} · {race.circuit.name}
      {race.laps ? ` · ${race.laps} laps` : ''}
    </>
  )
}

/** One Grand Prix as a category replays it: who is left, where they finished, what they scored. */
export function UniverseRacePage({ universe }: { universe: Universe }) {
  const { year: rawYear = '', round: rawRound = '' } = useParams()
  const year = Number(rawYear)
  const round = Number(rawRound)
  const { chosen, summary } = universe.use()
  const race = useApi<RaceDetail>(['race', year, round], () =>
    api.GET('/api/v1/races/{year}/{round_number}', {
      params: { path: { year, round_number: round } },
    }),
  )

  return (
    <section>
      <header className="page-head">
        <h1>
          {race.data?.official_name ?? `${rawYear} round ${rawRound}`} {universe.suffix}
        </h1>
        <p className="page-head__aside">
          <Link to={`/races/${year}/${round}`}>As it really happened →</Link>
        </p>
      </header>
      {race.data ? (
        <p className="muted">
          <Facts race={race.data} />
        </p>
      ) : null}
      {summary}
      {chosen ? <UniverseRace universe={universe} year={year} round={round} /> : null}
    </section>
  )
}

function UniverseRace({
  universe,
  year,
  round,
}: {
  universe: Universe
  year: number
  round: number
}) {
  const { results, rows, skipped } = useUniverseResults(universe, year)
  if (skipped.includes(round)) {
    return (
      <p className="state state--empty">This Grand Prix never happened {universe.suffix}.</p>
    )
  }
  if (!rows) return <DataState query={results}>{() => null}</DataState>
  const here = rows.filter((row) => row.round === round)
  const sprint = here.filter((row) => row.session_type === 'SPRINT_RACE')
  // The FP Championship's results are its free practice sessions, each a race.
  const practice = [...new Set(here.map((row) => row.session_type))]
    .filter((session) => session.startsWith('PRACTICE'))
    .sort()
  if (practice.length) {
    return (
      <>
        {practice.map((session) => (
          <section key={session}>
            <h2 className="section-title">Free practice {session.slice(-1)}</h2>
            <ResultsTable rows={here.filter((row) => row.session_type === session)} />
          </section>
        ))}
      </>
    )
  }
  return (
    <>
      <h2 className="section-title">Race</h2>
      {here.length ? (
        <ResultsTable rows={here.filter((row) => row.session_type === 'RACE')} />
      ) : (
        <p className="state state--empty">No results {universe.suffix}.</p>
      )}
      {sprint.length > 0 ? (
        <>
          <h2 className="section-title">Sprint</h2>
          <ResultsTable rows={sprint} />
        </>
      ) : null}
    </>
  )
}

function ResultsTable({ rows }: { rows: ResultItem[] }) {
  const to = useUniversePath()
  return (
    <table className="grid">
      <thead>
        <tr>
          <th className="num">Pos</th>
          <th>Driver</th>
          <th>Constructor</th>
          <th className="num">Grid</th>
          <th className="num">Laps</th>
          <th>Time or reason</th>
          <th className="num">Points</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.driver.slug}-${row.constructor.slug}`}>
            <td className="num">{row.position.number ?? row.position.text ?? '—'}</td>
            <td>
              <Link to={to(`/drivers/${row.driver.slug}`)}>{row.driver.name}</Link>
              {row.fastest_lap ? (
                <span className="badge badge--fl" title="Set the fastest lap">
                  FL
                </span>
              ) : null}
              {row.shared_car ? (
                <span className="badge" title="Shared the car">
                  shared
                </span>
              ) : null}
            </td>
            <td className="muted">
              <Link to={to(`/constructors/${row.constructor.slug}`)}>
                {row.constructor.name}
              </Link>
            </td>
            <td className="num muted">{row.grid_position ?? '—'}</td>
            <td className="num muted">{row.laps ?? '—'}</td>
            <td className="muted">{row.time ?? row.gap ?? row.reason_retired ?? '—'}</td>
            <td className="num">{formatPoints(row.points, '')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
